import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import i18next from 'i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/presentation/components/common';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import { ClinicMigrations } from '@/presentation/components/migracionFrontmy/ClinicMigrations';
import {
  REPORT_KINDS,
  analyzeMigration,
  downloadMigrationLogs,
  getClinicState,
  getMigration,
  startMigration,
  type ClinicState,
  MigracionApiError,
  type FileIssue,
  type MigrationJob,
  type ReportKind,
  type RunParams,
  type RunSummary,
  type SummaryItem,
} from '@/infrastructure/api/migracionFrontmyApi';

const K = 'pages.migracionFrontmy';
const POLL_MS = 2000;
const EMPTY_FILES: Record<ReportKind, File[]> = { pacientes: [], caja: [], agenda: [], gestion: [] };

function apiErrorText(err: unknown): string {
  if (err instanceof MigracionApiError && err.codigo) {
    return i18next.t(`${K}.apiErrors.${err.codigo}`, { defaultValue: err.message || err.codigo, detail: err.message });
  }
  return err instanceof Error && err.message ? err.message : i18next.t(`${K}.apiErrors.generic`);
}

function issueText(issue: FileIssue): string {
  return i18next.t(`${K}.fileIssues.${issue.codigo}`, {
    defaultValue: issue.codigo,
    file: issue.archivo ?? '',
    report: i18next.t(`${K}.reports.${issue.pareceSer ?? issue.reporte}.title`),
  });
}

/** Misma forma canónica que usa la API para comparar la prueba con lo que se va a aplicar. */
function canonParams(p: RunParams): string {
  return JSON.stringify({
    migrarContacto: p.migrarContacto,
    forzar: p.forzar,
    profesionales: Object.entries(p.profesionales).sort(([a], [b]) => a.localeCompare(b)),
    saldos: [...p.saldos].sort((a, b) => a.ficha.localeCompare(b.ficha)),
    consultorioId: p.consultorioId,
  });
}

/** Fecha civil ISO (yyyy-mm-dd) en el formato del idioma elegido. */
function civilDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(i18next.language);
}

function motivoText(code: string): string {
  return i18next.t(`${K}.motivos.${code}`, { defaultValue: code });
}

// ---------------------------------------------------------------------------
// Paso 1: casillero por reporte
// ---------------------------------------------------------------------------

const ReportSlot: React.FC<{
  kind: ReportKind;
  files: File[];
  issues: FileIssue[];
  disabled: boolean;
  onChange: (files: File[]) => void;
}> = ({ kind, files, issues, disabled, onChange }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [help, setHelp] = useState(false);

  const add = (list: FileList | null) => {
    if (!list?.length) return;
    // Orden natural: agenda1 antes que agenda2 antes que agenda10.
    const next = [...files, ...Array.from(list)].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
    );
    onChange(next);
  };

  const columns = t(`${K}.reports.${kind}.columns`);

  return (
    <div className={`panel-card p-4 sm:p-5 ${issues.length ? 'border-rose-300 dark:border-rose-700' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`${K}.reports.${kind}.title`)}</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.reports.${kind}.description`)}</p>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs panel-link underline underline-offset-2"
          onClick={() => setHelp((v) => !v)}
        >
          {help ? t(`${K}.hideHelp`) : t(`${K}.howToExport`)}
        </button>
      </div>

      {help && (
        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-700 dark:bg-gray-900 dark:text-slate-300">
          <p>{t(`${K}.reports.${kind}.howTo`)}</p>
          <p className="mt-2">
            <span className="font-semibold">{t(`${K}.columnsLabel`)}</span> {columns}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>{t(`${K}.generalTips.format`)}</li>
            <li>{t(`${K}.generalTips.split`)}</li>
            <li>{t(`${K}.generalTips.totals`)}</li>
          </ul>
        </div>
      )}

      <div
        className={`mt-3 rounded-xl border-2 border-dashed px-3 py-4 text-center text-xs transition-colors ${
          drag ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-slate-200 dark:border-gray-600'
        } ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (!disabled) add(e.dataTransfer.files);
        }}
      >
        <span className="text-slate-600 dark:text-slate-300">{t(`${K}.dropHere`)}</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".xlsx,.csv,.xls"
          className="hidden"
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {files.length > 0 && (
        <ol className="mt-3 space-y-1.5">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate text-slate-700 dark:text-slate-200">
                {i + 1}. {f.name}
              </span>
              {!disabled && (
                <button
                  type="button"
                  className="shrink-0 text-rose-600 hover:text-rose-800 dark:text-rose-400"
                  onClick={() => onChange(files.filter((_, j) => j !== i))}
                  aria-label={t(`${K}.removeFile`, { file: f.name })}
                  title={t(`${K}.removeFile`, { file: f.name })}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {issues.map((issue, i) => (
        <div key={i} className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {issueText(issue)}
          {issue.detalle && <div className="mt-1 break-words font-mono text-[11px] opacity-80">{issue.detalle}</div>}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Resumen de una corrida (prueba o aplicación)
// ---------------------------------------------------------------------------

const LEVEL_STYLE: Record<SummaryItem['nivel'], { icon: string; cls: string }> = {
  ok: { icon: '✓', cls: 'text-emerald-700 dark:text-emerald-300' },
  aviso: { icon: '!', cls: 'text-amber-700 dark:text-amber-300' },
  info: { icon: 'i', cls: 'text-sky-700 dark:text-sky-300' },
};

const SummaryView: React.FC<{ summary: RunSummary; jobId: string }> = ({ summary, jobId }) => {
  const { t } = useTranslation();
  const { error: showError } = useToast();
  const [downloading, setDownloading] = useState(false);
  const done = summary.items.filter((i) => i.nivel !== 'aviso');
  const pending = summary.items.filter((i) => i.nivel === 'aviso');
  const isTest = summary.modo === 'prueba';

  const download = async () => {
    setDownloading(true);
    try {
      await downloadMigrationLogs(jobId);
    } catch (err) {
      showError(apiErrorText(err));
    } finally {
      setDownloading(false);
    }
  };

  const itemText = (item: SummaryItem) =>
    t(`${K}.summary.items.${item.codigo}`, {
      defaultValue: item.codigo,
      count: item.cantidad,
      total: item.total ?? 0,
    });

  return (
    <div className="space-y-3 sm:space-y-3.5">
      <div className="panel-card p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          {isTest ? t(`${K}.summary.doneTitleTest`) : t(`${K}.summary.doneTitle`)}
        </h3>
        <ul className="mt-3 space-y-2">
          {done.map((item) => (
            <li key={item.codigo} className="flex gap-2 text-sm">
              <span className={`w-4 shrink-0 text-center font-bold ${LEVEL_STYLE[item.nivel].cls}`}>{LEVEL_STYLE[item.nivel].icon}</span>
              <span className="text-slate-700 dark:text-slate-200">{itemText(item)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel-card p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`${K}.summary.pendingTitle`)}</h3>
        {pending.length === 0 ? (
          <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">{t(`${K}.summary.nothingPending`)}</p>
        ) : (
          <>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.summary.pendingHint`)}</p>
            <ul className="mt-3 space-y-3">
              {pending.map((item) => (
                <li key={item.codigo} className="text-sm">
                  <div className="flex gap-2">
                    <span className={`w-4 shrink-0 text-center font-bold ${LEVEL_STYLE.aviso.cls}`}>!</span>
                    <div className="min-w-0">
                      <p className="text-slate-800 dark:text-slate-100">{itemText(item)}</p>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {t(`${K}.summary.todo.${item.codigo}`, {
                          defaultValue: t(`${K}.summary.todo.CONFLICTO`),
                        })}
                      </p>
                      {item.motivos && item.motivos.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
                          {item.motivos.map((m) => (
                            <li key={m.codigo}>
                              · {motivoText(m.codigo)}: <span className="font-semibold tabular-nums">{m.cantidad}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {item.archivo && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {t(`${K}.summary.detailIn`)} <span className="font-mono">{item.archivo}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="mt-4">
          <Button variant="secondary" size="sm" onClick={download} isLoading={downloading}>
            {t(`${K}.downloadLogs`)}
          </Button>
        </div>
      </div>

      {summary.turnosPendientes.length > 0 && (
        <div className="panel-table-wrap">
          <div className="px-4 pt-4 sm:px-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`${K}.summary.pendingTurnsTitle`)}</h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {summary.turnosPendientesTotal > summary.turnosPendientes.length
                ? t(`${K}.summary.pendingTurnsTruncated`, {
                    shown: summary.turnosPendientes.length,
                    total: summary.turnosPendientesTotal,
                  })
                : t(`${K}.summary.pendingTurnsHint`)}
            </p>
          </div>
          <table className="mt-3 min-w-full text-xs">
            <thead className="panel-thead">
              <tr className="text-left text-slate-500 dark:text-slate-400">
                <th className="px-3 py-2">{t(`${K}.table.patient`)}</th>
                <th className="px-3 py-2">{t(`${K}.table.date`)}</th>
                <th className="px-3 py-2">{t(`${K}.table.time`)}</th>
                <th className="px-3 py-2">{t(`${K}.table.service`)}</th>
                <th className="px-3 py-2">{t(`${K}.table.reason`)}</th>
                <th className="px-3 py-2">{t(`${K}.table.source`)}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {summary.turnosPendientes.map((p, i) => (
                <tr key={i} className="text-slate-700 dark:text-slate-200">
                  <td className="px-3 py-1.5">{p.paciente}</td>
                  <td className="px-3 py-1.5 tabular-nums">{p.fecha}</td>
                  <td className="px-3 py-1.5 tabular-nums">{p.hora}</td>
                  <td className="px-3 py-1.5">{p.servicio}</td>
                  <td className="px-3 py-1.5">{motivoText(p.motivo)}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">
                    {p.archivo}:{p.fila}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3 | 4;

export const MigracionFrontmyPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = useAuth();
  const { error: showError, success } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobIdParam = searchParams.get('job');

  const [tenants, setTenants] = useState<TenantGridDTO[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [files, setFiles] = useState<Record<ReportKind, File[]>>(EMPTY_FILES);
  const [analyzing, setAnalyzing] = useState(false);
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [starting, setStarting] = useState(false);

  const [migrarContacto, setMigrarContacto] = useState(false);
  const [forzar, setForzar] = useState(false);
  const [profesionales, setProfesionales] = useState<Record<string, number>>({});
  const [saldos, setSaldos] = useState<Record<string, { saldo: string; motivo: string }>>({});
  const [showSaldos, setShowSaldos] = useState(false);
  const [consultorioId, setConsultorioId] = useState<number | null>(null);
  /** Consultorios y migraciones ya aplicadas de la clínica elegida (para deshacer). */
  const [clinicState, setClinicState] = useState<ClinicState | null>(null);
  const [clinicStateVersion, setClinicStateVersion] = useState(0);

  useEffect(() => {
    httpClient
      .get<TenantGridDTO[]>('/tenants')
      // Más nuevas primero: la que se va a migrar suele ser la recién creada (fechaActivoUTC = creado_en).
      .then((res) =>
        setTenants([...res.data].sort((a, b) => Date.parse(b.fechaActivoUTC) - Date.parse(a.fechaActivoUTC))),
      )
      .catch(() => showError(i18next.t(`${K}.loadTenantsError`)));
  }, [showError]);

  /** Carga una migración existente (?job=) y restaura las opciones de la última prueba. */
  const adoptJob = useCallback((next: MigrationJob, restoreParams: boolean) => {
    setJob(next);
    setTenantId(next.tenantId);
    if (!restoreParams) return;
    const params = next.ultimosParams ?? next.ultimaPrueba?.params;
    if (params) {
      setMigrarContacto(params.migrarContacto);
      setForzar(params.forzar);
      setConsultorioId(params.consultorioId ?? null);
      setProfesionales(params.profesionales);
      setSaldos(Object.fromEntries(params.saldos.map((s) => [s.ficha, { saldo: s.saldo, motivo: s.motivo }])));
    } else {
      setProfesionales(
        Object.fromEntries(
          next.analisis.profesionales.filter((p) => p.sugeridoId != null).map((p) => [p.clave, p.sugeridoId as number]),
        ),
      );
      // Con un solo consultorio no hay nada que elegir.
      const consultorios = next.analisis.clinica?.consultorios ?? [];
      setConsultorioId(consultorios.length === 1 ? consultorios[0].id : null);
    }
  }, []);

  // Al elegir clínica (o después de deshacer), traemos sus consultorios y migraciones aplicadas.
  useEffect(() => {
    if (!tenantId) {
      setClinicState(null);
      return;
    }
    let cancelled = false;
    getClinicState(tenantId)
      .then((state) => {
        if (!cancelled) setClinicState(state);
      })
      .catch(() => {
        if (!cancelled) setClinicState(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, clinicStateVersion, job?.estado]);

  useEffect(() => {
    if (!jobIdParam || job?.id === jobIdParam) return;
    getMigration(jobIdParam)
      .then((j) => adoptJob(j, true))
      .catch((err) => {
        showError(apiErrorText(err));
        setSearchParams({});
      });
  }, [jobIdParam, job?.id, adoptJob, showError, setSearchParams]);

  // Mientras corre la prueba/aplicación, consultamos el avance.
  useEffect(() => {
    if (job?.estado !== 'ejecutando') return;
    const timer = window.setInterval(() => {
      getMigration(job.id)
        .then((next) => {
          setJob(next);
          if (next.estado === 'aplicado') success(i18next.t(`${K}.appliedToast`));
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [job?.id, job?.estado, success]);

  const params: RunParams = useMemo(
    () => ({
      migrarContacto,
      forzar,
      profesionales,
      saldos: Object.entries(saldos)
        .filter(([, v]) => v.saldo.trim() !== '' || v.motivo.trim() !== '')
        .map(([ficha, v]) => ({ ficha, saldo: v.saldo.trim(), motivo: v.motivo.trim() })),
      consultorioId,
    }),
    [migrarContacto, forzar, profesionales, saldos, consultorioId],
  );

  if (!canAdminister) return <AccessDenied />;

  const analisis = job?.analisis;
  const hasFileErrors = !!analisis?.errores.length;
  const consultorios = analisis?.clinica?.consultorios ?? [];
  const chosenConsultorio = consultorios.find((c) => c.id === consultorioId) ?? null;
  const consultorioHasData = !!chosenConsultorio && (chosenConsultorio.turnos > 0 || chosenConsultorio.movimientos > 0);
  const unmapped = analisis?.profesionales.filter((p) => profesionales[p.clave] == null) ?? [];
  const noClinicProfessionals = !!analisis && analisis.profesionalesClinica.length === 0;
  const invalidSaldos = params.saldos.filter((s) => !/^-?\d+(\.\d{1,2})?$/.test(s.saldo) || !s.motivo);
  const canRunTest =
    !!job && !hasFileErrors && !unmapped.length && !noClinicProfessionals && !invalidSaldos.length &&
    !!chosenConsultorio && (!consultorioHasData || forzar) && job.estado !== 'ejecutando' && job.estado !== 'aplicado';
  const lastTest = job?.ultimaPrueba;
  const testMatchesParams = !!lastTest && canonParams(lastTest.params) === canonParams(params);

  const step: Step = !job || hasFileErrors ? 1 : job.estado === 'aplicado' ? 4 : lastTest || job.estado === 'ejecutando' ? 3 : 2;
  const locked = !!job && (job.estado === 'ejecutando' || job.estado === 'aplicado');
  const allFilesChosen = REPORT_KINDS.every((k) => files[k].length > 0);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const next = await analyzeMigration(tenantId, files);
      adoptJob(next, true);
      setSearchParams({ job: next.id });
    } catch (err) {
      showError(apiErrorText(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRun = async (modo: 'prueba' | 'aplicar') => {
    if (!job) return;
    if (modo === 'aplicar' && !window.confirm(i18next.t(`${K}.applyConfirm`, { clinic: job.tenantNombre }))) return;
    setStarting(true);
    try {
      setJob(await startMigration(job.id, modo, params));
    } catch (err) {
      showError(apiErrorText(err));
    } finally {
      setStarting(false);
    }
  };

  const handleRestart = () => {
    setJob(null);
    setFiles(EMPTY_FILES);
    setProfesionales({});
    setSaldos({});
    setForzar(false);
    setMigrarContacto(false);
    setConsultorioId(null);
    setSearchParams({});
  };

  const issuesFor = (kind: ReportKind) => analisis?.errores.filter((e) => e.reporte === kind) ?? [];
  const stepLabels = [t(`${K}.steps.files`), t(`${K}.steps.review`), t(`${K}.steps.test`), t(`${K}.steps.apply`)];

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t(`${K}.title`)}</h1>
          <p className="panel-page-subtitle hidden sm:block">{t(`${K}.subtitle`)}</p>
        </div>
        {job && (
          <Button variant="secondary" size="sm" onClick={handleRestart} disabled={job.estado === 'ejecutando'}>
            {t(`${K}.newMigration`)}
          </Button>
        )}
      </div>

      <ol className="flex flex-wrap gap-2 text-xs">
        {stepLabels.map((label, i) => {
          const n = (i + 1) as Step;
          const cls =
            n === step
              ? 'bg-teal-600 text-white'
              : n < step
                ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                : 'bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400';
          return (
            <li key={label} className={`panel-chip ${cls}`}>
              {n}. {label}
            </li>
          );
        })}
      </ol>

      {/* Paso 1: clínica y archivos */}
      {step === 1 && (
        <>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm leading-relaxed text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 sm:px-5">
            <p className="font-semibold">{t(`${K}.before.title`)}</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>{t(`${K}.before.clinic`)}</li>
              <li>{t(`${K}.before.professionals`)}</li>
              <li>{t(`${K}.before.files`)}</li>
            </ul>
          </div>

          <div className="panel-card p-4 sm:p-5">
            <label className="block text-sm font-semibold text-slate-900 dark:text-white" htmlFor="migracion-clinic">
              {t(`${K}.clinicLabel`)}
            </label>
            <select
              id="migracion-clinic"
              className="panel-focus mt-2 w-full max-w-md rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={analyzing}
            >
              <option value="">{t(`${K}.clinicPlaceholder`)}</option>
              {tenants.map((tn) => {
                // Hay clínicas con el mismo nombre (y hasta el mismo admin): la fecha de alta las distingue.
                const repeated = tenants.filter((o) => o.nombre === tn.nombre).length > 1;
                const created = new Date(tn.fechaActivoUTC).toLocaleString(i18next.language, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                });
                return (
                  <option key={String(tn.id)} value={String(tn.id).toLowerCase()}>
                    {repeated ? t(`${K}.clinicOption`, { name: tn.nombre, created }) : tn.nombre}
                  </option>
                );
              })}
            </select>
          </div>

          {clinicState && clinicState.tenantId === tenantId.toLowerCase() && (
            <ClinicMigrations
              tenantId={clinicState.tenantId}
              tenantNombre={clinicState.tenantNombre}
              lotes={clinicState.lotes}
              onChanged={() => setClinicStateVersion((v) => v + 1)}
            />
          )}

          <div className="grid items-start gap-3 md:grid-cols-2">
            {REPORT_KINDS.map((kind) => (
              <ReportSlot
                key={kind}
                kind={kind}
                files={files[kind]}
                issues={issuesFor(kind)}
                disabled={analyzing}
                onChange={(next) => setFiles((prev) => ({ ...prev, [kind]: next }))}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button onClick={handleAnalyze} isLoading={analyzing} disabled={!tenantId || !allFilesChosen}>
              {analyzing ? t(`${K}.analyzing`) : t(`${K}.analyze`)}
            </Button>
            {analyzing ? (
              <span className="text-xs text-slate-500 dark:text-slate-400">{t(`${K}.analyzingHint`)}</span>
            ) : (
              (!tenantId || !allFilesChosen) && (
                <span className="text-xs text-slate-500 dark:text-slate-400">{t(`${K}.analyzeHint`)}</span>
              )
            )}
          </div>
        </>
      )}

      {/* Paso 2+: revisión y opciones */}
      {job && analisis && !hasFileErrors && (
        <>
          <div className="panel-card p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              <Trans i18nKey={`${K}.review.title`} values={{ clinic: job.tenantNombre }} components={{ b: <strong /> }} />
            </h3>
            {analisis.previa && (
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(
                  [
                    ['patients', analisis.previa.personas],
                    ['withDni', analisis.previa.conDni],
                    ['appointments', analisis.previa.turnos],
                    ['payments', analisis.previa.movimientos],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key}>
                    <p className="panel-stat-label">{t(`${K}.review.stats.${key}`)}</p>
                    <p className="panel-stat-value">{value.toLocaleString(i18next.language)}</p>
                  </div>
                ))}
              </div>
            )}
            <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-300">
              {REPORT_KINDS.map((kind) =>
                analisis.archivos[kind].map((f) => (
                  <li key={`${kind}-${f.nombre}`}>
                    <span className="font-medium">{t(`${K}.reports.${kind}.title`)}:</span> {f.nombre} —{' '}
                    {t(`${K}.review.rows`, { count: f.filas })}
                    {f.desde && f.hasta && ` (${t(`${K}.review.range`, { from: civilDate(f.desde), to: civilDate(f.hasta) })})`}
                  </li>
                )),
              )}
            </ul>
          </div>

          <div className="panel-card p-4 sm:p-5">
            <label className="block text-sm font-semibold text-slate-900 dark:text-white" htmlFor="migracion-consultorio">
              {t(`${K}.consultorio.title`)}
            </label>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.consultorio.hint`)}</p>
            <select
              id="migracion-consultorio"
              className={`panel-focus mt-2 w-full max-w-md rounded-md border bg-white px-3 py-2 text-sm dark:bg-gray-800 dark:text-white ${
                consultorioId == null ? 'border-rose-300 dark:border-rose-700' : 'border-slate-200 dark:border-gray-600'
              }`}
              value={consultorioId ?? ''}
              disabled={locked}
              onChange={(e) => {
                setConsultorioId(e.target.value ? Number(e.target.value) : null);
                setForzar(false);
              }}
            >
              <option value="">{t(`${K}.consultorio.choose`)}</option>
              {consultorios.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.turnos || c.movimientos
                    ? t(`${K}.consultorio.withData`, { name: c.nombre, turnos: c.turnos, caja: c.movimientos })
                    : t(`${K}.consultorio.empty`, { name: c.nombre })}
                </option>
              ))}
            </select>
            {analisis.clinica && analisis.clinica.pacientes > 0 && (
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">
                {t(`${K}.consultorio.reuse`, {
                  existing: analisis.clinica.pacientes,
                  count: analisis.clinica.reutilizables,
                })}
              </p>
            )}
            {consultorioHasData && chosenConsultorio && (
              <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2.5 text-sm text-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                <p className="font-semibold">{t(`${K}.consultorio.hasDataTitle`)}</p>
                <p className="mt-1">
                  {t(`${K}.consultorio.hasDataDetail`, { turnos: chosenConsultorio.turnos, caja: chosenConsultorio.movimientos })}
                </p>
                <label className="mt-2 flex items-start gap-2">
                  <input type="checkbox" checked={forzar} disabled={locked} onChange={(e) => setForzar(e.target.checked)} className="mt-0.5" />
                  <span>{t(`${K}.consultorio.force`)}</span>
                </label>
              </div>
            )}
          </div>

          <div className="panel-card p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`${K}.options.title`)}</h3>
            <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={migrarContacto}
                disabled={locked}
                onChange={(e) => setMigrarContacto(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                {t(`${K}.options.contact`)}
                <span className="block text-xs text-slate-500 dark:text-slate-400">{t(`${K}.options.contactHint`)}</span>
              </span>
            </label>
          </div>

          <div className="panel-card p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`${K}.professionals.title`)}</h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.professionals.hint`)}</p>
            {noClinicProfessionals ? (
              <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                {t(`${K}.professionals.noneInClinic`)}
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="panel-thead">
                    <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                      <th className="px-3 py-2">{t(`${K}.professionals.frontmyName`)}</th>
                      <th className="px-3 py-2">{t(`${K}.professionals.appointments`)}</th>
                      <th className="px-3 py-2">{t(`${K}.professionals.coofitProfessional`)}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                    {analisis.profesionales.map((p) => (
                      <tr key={p.clave}>
                        <td className="px-3 py-2 text-slate-800 dark:text-slate-100">
                          {p.nombre || <em className="text-slate-500">{t(`${K}.professionals.noName`)}</em>}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-600 dark:text-slate-300">{p.turnos}</td>
                        <td className="px-3 py-2">
                          <select
                            className={`panel-focus w-full min-w-[12rem] rounded-md border px-2 py-1.5 text-sm dark:bg-gray-800 dark:text-white ${
                              profesionales[p.clave] == null ? 'border-rose-300 dark:border-rose-700' : 'border-slate-200 dark:border-gray-600'
                            }`}
                            value={profesionales[p.clave] ?? ''}
                            disabled={locked}
                            aria-label={t(`${K}.professionals.selectFor`, { name: p.nombre || t(`${K}.professionals.noName`) })}
                            onChange={(e) =>
                              setProfesionales((prev) => {
                                const next = { ...prev };
                                if (e.target.value) next[p.clave] = Number(e.target.value);
                                else delete next[p.clave];
                                return next;
                              })
                            }
                          >
                            <option value="">{t(`${K}.professionals.choose`)}</option>
                            {analisis.profesionalesClinica.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nombre}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {analisis.saldosAConfirmar.length > 0 && (
            <div className="panel-card p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                {t(`${K}.balances.title`, { count: analisis.saldosAConfirmar.length })}
              </h3>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.balances.hint`)}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button variant="secondary" size="sm" onClick={() => setShowSaldos((v) => !v)}>
                  {showSaldos ? t(`${K}.balances.hide`) : t(`${K}.balances.show`)}
                </Button>
                {params.saldos.length > 0 && (
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    {t(`${K}.balances.confirmedCount`, { count: params.saldos.length })}
                  </span>
                )}
              </div>
              {showSaldos && (
              <div className="mt-3 max-h-[28rem] overflow-auto">
                <table className="min-w-full text-xs">
                  <thead className="panel-thead">
                    <tr className="text-left text-slate-500 dark:text-slate-400">
                      <th className="px-3 py-2">{t(`${K}.balances.patient`)}</th>
                      <th className="px-3 py-2">{t(`${K}.balances.record`)}</th>
                      <th className="px-3 py-2">{t(`${K}.balances.calculated`)}</th>
                      <th className="px-3 py-2">{t(`${K}.balances.issues`)}</th>
                      <th className="px-3 py-2">{t(`${K}.balances.confirmedBalance`)}</th>
                      <th className="px-3 py-2">{t(`${K}.balances.reason`)}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                    {analisis.saldosAConfirmar.map((s) => {
                      const value = saldos[s.ficha] ?? { saldo: '', motivo: '' };
                      const set = (patch: Partial<typeof value>) =>
                        setSaldos((prev) => ({ ...prev, [s.ficha]: { ...value, ...patch } }));
                      return (
                        <tr key={s.ficha} className="align-top text-slate-700 dark:text-slate-200">
                          <td className="px-3 py-2">{s.paciente}</td>
                          <td className="px-3 py-2 tabular-nums">{s.ficha}</td>
                          <td className="px-3 py-2 tabular-nums">{s.saldoCalculado}</td>
                          <td className="px-3 py-2">{s.incidencias.map(motivoText).join(' · ')}</td>
                          <td className="px-3 py-2">
                            <input
                              className="panel-focus w-24 rounded-md border border-slate-200 px-2 py-1 dark:border-gray-600 dark:bg-gray-800"
                              inputMode="decimal"
                              placeholder="0.00"
                              value={value.saldo}
                              disabled={locked}
                              aria-label={t(`${K}.balances.confirmedBalance`)}
                              onChange={(e) => set({ saldo: e.target.value.replace(',', '.') })}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              className="panel-focus w-40 rounded-md border border-slate-200 px-2 py-1 dark:border-gray-600 dark:bg-gray-800"
                              placeholder={t(`${K}.balances.reasonPlaceholder`)}
                              value={value.motivo}
                              disabled={locked}
                              aria-label={t(`${K}.balances.reason`)}
                              onChange={(e) => set({ motivo: e.target.value })}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
              {invalidSaldos.length > 0 && (
                <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">{t(`${K}.balances.invalid`)}</p>
              )}
            </div>
          )}

          {/* Acción principal del paso */}
          {job.estado !== 'aplicado' && (
            <div className="panel-card p-4 sm:p-5">
              {unmapped.length > 0 && (
                <p className="mb-3 text-xs text-rose-700 dark:text-rose-300">
                  {t(`${K}.professionals.missing`, { count: unmapped.length })}
                </p>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  variant={lastTest && testMatchesParams ? 'secondary' : 'primary'}
                  onClick={() => handleRun('prueba')}
                  isLoading={starting || job.ejecutando?.modo === 'prueba'}
                  disabled={!canRunTest || starting}
                >
                  {lastTest ? t(`${K}.runTestAgain`) : t(`${K}.runTest`)}
                </Button>
                {lastTest && (
                  <Button
                    variant="danger"
                    onClick={() => handleRun('aplicar')}
                    isLoading={job.ejecutando?.modo === 'aplicar'}
                    disabled={!testMatchesParams || job.estado === 'ejecutando' || starting}
                  >
                    {t(`${K}.apply`)}
                  </Button>
                )}
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {lastTest && !testMatchesParams ? t(`${K}.paramsChanged`) : t(`${K}.testHint`)}
              </p>
            </div>
          )}

          {job.estado === 'ejecutando' && (
            <div className="panel-card p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                {job.ejecutando?.modo === 'aplicar' ? t(`${K}.running.apply`) : t(`${K}.running.test`)}
              </h3>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.running.hint`)}</p>
              <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
                {job.lineas.slice(-80).join('\n') || '…'}
              </pre>
            </div>
          )}

          {job.estado === 'error' && job.error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200 sm:px-5">
              <p className="font-semibold">
                {job.error.modo === 'aplicar' ? t(`${K}.failed.apply`) : t(`${K}.failed.test`)}
              </p>
              <p className="mt-1 break-words font-mono text-xs">{job.error.mensaje}</p>
              <div className="mt-3">
                <Button variant="secondary" size="sm" onClick={() => downloadMigrationLogs(job.id).catch((e) => showError(apiErrorText(e)))}>
                  {t(`${K}.downloadLogs`)}
                </Button>
              </div>
            </div>
          )}

          {job.aplicacion ? (
            <>
              {job.aplicacion.deshecha ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 sm:px-5">
                  <Trans
                    i18nKey={`${K}.undoneBanner`}
                    values={{ clinic: job.tenantNombre, date: new Date(job.aplicacion.deshecha.en).toLocaleString(i18next.language) }}
                    components={{ b: <strong /> }}
                  />
                </div>
              ) : (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200 sm:px-5">
                  <Trans i18nKey={`${K}.appliedBanner`} values={{ clinic: job.tenantNombre }} components={{ b: <strong /> }} />
                </div>
              )}
              {clinicState && (
                <ClinicMigrations
                  tenantId={clinicState.tenantId}
                  tenantNombre={clinicState.tenantNombre}
                  lotes={clinicState.lotes}
                  onChanged={() => {
                    setClinicStateVersion((v) => v + 1);
                    getMigration(job.id).then(setJob).catch(() => undefined);
                  }}
                />
              )}
              <SummaryView summary={job.aplicacion.resumen} jobId={job.id} />
            </>
          ) : (
            lastTest && job.estado !== 'ejecutando' && <SummaryView summary={lastTest.resumen} jobId={job.id} />
          )}
        </>
      )}
    </div>
  );
};
